import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function tone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'read') return 'bg-slate-100 text-slate-700';
  if (s === 'critical') return 'bg-red-100 text-red-800';
  if (s === 'warning') return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-800';
}

function iconForType(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('overdue') || t.includes('warning')) return '!';
  return 'i';
}

function relatedPath(notification) {
  const type = String(notification?.type || '').toLowerCase();
  if (type.includes('loan')) return '/dashboard/budget/loans';
  if (type.includes('credit')) return '/dashboard/budget/credit';
  if (type.includes('expense_request')) return '/dashboard/budget/expense-requests';
  if (type.includes('subscription')) return '/dashboard/package';
  if (type.includes('summary')) return '/dashboard/budget/reports';
  if (type.includes('cash_flow') || type.includes('low_balance') || type.includes('low_savings') || type.includes('budget_warning')) return '/dashboard/budget/cash-flow';
  return '/dashboard/budget';
}

export default function BudgetNotifications() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        month: nextFilters.month || undefined,
        year: nextFilters.year || undefined,
        branch_id: nextFilters.branch_id && nextFilters.branch_id !== 'all' ? nextFilters.branch_id : undefined,
      };
      const res = await api.get('/budget/notifications', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load notifications');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const notifications = Array.isArray(data?.notifications) ? data.notifications : [];
  const summary = data?.summary || {};
  const filtered = notifications.filter((item) => {
    const bucket = item.filter_bucket || 'info';
    if (filters.type === 'all') return true;
    if (filters.type === 'read') return item.read;
    if (filters.type === 'unread') return !item.read;
    if (filters.type === 'critical') return bucket === 'critical';
    if (filters.type === 'warning') return bucket === 'warning';
    if (filters.type === 'info') return bucket === 'info';
    return true;
  });

  const markRead = async (item) => {
    try {
      await api.post(`/budget/notifications/${item.id}/read`);
      toast.success('Notification marked as read');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/budget/notifications/read-all');
      toast.success('All notifications marked as read');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to mark all as read');
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
          <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">{data?.message || 'Budget access is required.'}</div>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.assign('/dashboard/package')}>View Packages</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">Critical financial notifications and upgrade nudges.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={markAllRead}>Mark all as read</Button>
          <Button variant="outline" className="border-slate-200" onClick={load}>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total</p><p className="mt-2 text-2xl font-bold text-slate-900">{summary.total || 0}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Unread</p><p className="mt-2 text-2xl font-bold text-slate-900">{summary.unread || 0}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Critical</p><p className="mt-2 text-2xl font-bold text-slate-900">{summary.critical || 0}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Warnings</p><p className="mt-2 text-2xl font-bold text-slate-900">{summary.warning || 0}</p></CardContent></Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <Select value={filters.type} onValueChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}>
            <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))} placeholder="Month" className="bg-white" />
          <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))} placeholder="Year" className="bg-white" />
          {!singleBranch ? (
            <Select value={filters.branch_id} onValueChange={(value) => setFilters((prev) => ({ ...prev, branch_id: value }))}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">{activeBranches[0]?.name || 'Main Branch'}</div>
          )}
          <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-500">Showing budget alerts, reminders, and upgrade nudges.</div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {filtered.map((item) => (
          <Card key={item.id} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center font-semibold text-slate-600">{iconForType(item.filter_bucket)}</div>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{item.title}</h3>
                    <Badge className={tone(item.filter_bucket)}>{item.read ? 'Read' : 'Unread'}</Badge>
                    <Badge className={tone(item.filter_bucket)}>{item.filter_bucket}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{item.message}</p>
                  <div className="text-xs text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</div>
                </div>
              </div>
              <div className="flex gap-2">
                {!item.read ? <Button variant="outline" className="border-slate-200" onClick={() => markRead(item)}>Mark as read</Button> : null}
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.assign(relatedPath(item))}>Open related page</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-8 text-center text-slate-500">No budget notifications found.</CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
